/**
 * authApi — single RTK Query slice that owns every auth-adjacent fetch.
 *
 * Consolidates three previously-scattered call paths (AuthContext,
 * MFAGuard, MFALoginFlow) into one compound `getMyProfileAndRoles`
 * endpoint so the cache dedups automatically. Trusted-device lookups
 * and MFA factor/enroll/verify go through here too.
 *
 * Session + password sign-in are modeled as mutations so components
 * can `unwrap()` + `useSignIn` ergonomically without leaking the
 * Supabase client into the UI layer.
 */
import { rootApi } from "../../../lib/api/rootApi";
import { supabase } from "../../../lib/supabase";
import type {
  EnrollMfaArgs,
  EnrollMfaResult,
  MfaFactor,
  MfaFactorsSnapshot,
  ProfileAndRoles,
  UserProfile,
  UserProfileMinRow,
  UserRoleRow,
  VerifyMfaArgs,
} from "../types/authTypes";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function resolveRoles(rows: UserRoleRow[]): {
  primary: string | null;
  subRoles: string[];
} {
  const primaryRow = rows.find((r) => !r.roles?.is_subrole);
  const primary = primaryRow?.roles?.name ?? rows[0]?.roles?.name ?? null;
  const subRoles = rows
    .filter((r) => r.roles?.is_subrole)
    .map((r) => r.roles?.name)
    .filter((n): n is string => !!n);
  return { primary, subRoles };
}

function snapshotFactors(factors: {
  totp?: MfaFactor[] | null;
  phone?: MfaFactor[] | null;
  all?: MfaFactor[] | null;
} | null | undefined): MfaFactorsSnapshot {
  const totp = factors?.totp ?? [];
  const phone = factors?.phone ?? [];
  const all = factors?.all ?? [...totp, ...phone];
  let hasVerifiedTotp = totp.some((f) => f.status === "verified");
  let hasVerifiedPhone = phone.some((f) => f.status === "verified");
  if (!hasVerifiedTotp && !hasVerifiedPhone && all.length) {
    hasVerifiedTotp = all.some(
      (f) => f.factor_type === "totp" && f.status === "verified",
    );
    hasVerifiedPhone = all.some(
      (f) => f.factor_type === "phone" && f.status === "verified",
    );
  }
  return {
    totp,
    phone,
    all,
    hasVerifiedTotp,
    hasVerifiedPhone,
    hasMfa: hasVerifiedTotp || hasVerifiedPhone,
  };
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

interface ProfileAndRolesArgs {
  profileId: string;
}

export const authApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Single compound endpoint replacing:
     *   - AuthContext.loadUserProfile (AAL1 path) — profile + roles
     *   - AuthContext.loadUserProfile (AAL2 path) — full profile + roles
     *   - MFAGuard.checkDeviceTrusted profile/school lookup
     *   - MFALoginFlow.verifyCode post-verify profile fallback
     *
     * Cache key is `profileId`, so all callers hit the same cache slot.
     */
    getMyProfileAndRoles: builder.query<ProfileAndRoles, ProfileAndRolesArgs>({
      queryFn: async ({ profileId }, _queryApi, _extraOptions, _baseQuery) => {
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          // `aal` exists at runtime but isn't on the public Session type.
          const sessionAal = (session as unknown as { aal?: string } | null)
            ?.aal;
          const aal: "aal1" | "aal2" = sessionAal === "aal2" ? "aal2" : "aal1";

          // AAL1: cheap view. AAL2: full table. Both return the same domain
          // shape so consumers don't branch.
          //
          // The AAL1 view (`vw_react_user_profiles_min`) now surfaces
          // `sub_roles` directly (migration 20260419), so we no longer do a
          // separate `user_roles` round-trip on boot.
          if (aal === "aal1") {
            const { data: row, error: profileErr } = await supabase
              .from("vw_react_user_profiles_min")
              .select(
                "id, school_id, first_name, last_name, role_id, role_name, sub_roles, role_ids, group_ids",
              )
              .eq("id", profileId)
              .single();
            if (profileErr) throw profileErr;
            if (!row) throw new Error("No profile found for user (min)");
            const min = row as UserProfileMinRow;

            const profile: UserProfile = {
              id: min.id,
              school_id: min.school_id,
              first_name: min.first_name,
              last_name: min.last_name,
              role_id: min.role_id,
              role: min.role_name ?? undefined,
              sub_roles: min.sub_roles ?? [],
              role_ids: min.role_ids ?? [],
              group_ids: min.group_ids ?? [],
            };
            return { data: { profile, aal } };
          }

          // AAL2: full load
          const [profileRes, rolesRes] = await Promise.all([
            supabase
              .from("user_profiles")
              .select(
                "id, school_id, first_name, last_name, account_status, created_at, updated_at",
              )
              .eq("id", profileId)
              .single(),
            supabase
              .from("user_roles")
              .select(
                "role_id, roles:roles!user_roles_role_id_fkey(name, is_subrole)",
              )
              .eq("user_profile_id", profileId),
          ]);
          if (profileRes.error) throw profileRes.error;
          if (!profileRes.data) throw new Error("No profile found for user");
          if (rolesRes.error) throw rolesRes.error;
          if (!rolesRes.data?.length) throw new Error("No roles assigned to user");

          const { primary, subRoles } = resolveRoles(
            rolesRes.data as unknown as UserRoleRow[],
          );
          if (!primary) throw new Error("No primary role found for user");

          const base = profileRes.data as Omit<UserProfile, "role" | "sub_roles">;
          const profile: UserProfile = {
            ...base,
            role: primary,
            sub_roles: subRoles,
          };
          return { data: { profile, aal } };
        } catch (err) {
          const e = err as { message?: string };
          return {
            error: {
              message: e?.message ?? "Profile load failed",
              details: "",
              hint: "",
              code: "auth_profile_error",
            },
          };
        }
      },
      providesTags: (_r, _e, arg) => [{ type: "Auth", id: arg.profileId }],
      keepUnusedDataFor: 600,
    }),

    /** Lists MFA factors on the current session. Used by MFAGuard. */
    listMfaFactors: builder.query<MfaFactorsSnapshot, void>({
      queryFn: async () => {
        try {
          const { data, error } = await supabase.auth.mfa.listFactors();
          if (error) throw error;
          return { data: snapshotFactors(data as MfaFactorsSnapshot | null) };
        } catch (err) {
          const e = err as { message?: string };
          return {
            error: {
              message: e?.message ?? "MFA factor list failed",
              details: "",
              hint: "",
              code: "auth_mfa_error",
            },
          };
        }
      },
      providesTags: [{ type: "Auth", id: "mfa-factors" }],
      keepUnusedDataFor: 60,
    }),

    /** Starts TOTP enrollment — returns QR code + secret for the user to scan. */
    enrollTotp: builder.mutation<EnrollMfaResult, EnrollMfaArgs>({
      queryFn: async ({ friendlyName }) => {
        try {
          const { data, error } = await supabase.auth.mfa.enroll({
            factorType: "totp",
            friendlyName,
          });
          if (error) throw error;
          if (!data) throw new Error("MFA enroll returned no data");
          return {
            data: {
              id: data.id,
              qrCode: data.totp.qr_code,
              secret: data.totp.secret,
            },
          };
        } catch (err) {
          const e = err as { message?: string };
          return {
            error: {
              message: e?.message ?? "MFA enrollment failed",
              details: "",
              hint: "",
              code: "auth_mfa_error",
            },
          };
        }
      },
      invalidatesTags: [{ type: "Auth", id: "mfa-factors" }],
    }),

    /**
     * Verifies a TOTP code against the enrolled factor.
     *
     * TOTP is time-based, so the user always has a current code in their
     * authenticator app — we issue a fresh challenge AND verify in one
     * shot. Do NOT use this for SMS factors: it would call
     * `mfa.challenge` (sending a new SMS) at the same instant we verify,
     * so the user's typed code (from a previous SMS) would arrive
     * before they get the new one. Use `issueMfaChallenge` +
     * `verifyMfaWithChallenge` for SMS.
     */
    verifyTotp: builder.mutation<{ aal: string | null }, VerifyMfaArgs>({
      queryFn: async ({ factorId, code }) => {
        try {
          const challenge = await supabase.auth.mfa.challenge({ factorId });
          if (challenge.error) throw challenge.error;
          const verify = await supabase.auth.mfa.verify({
            factorId,
            challengeId: challenge.data.id,
            code,
          });
          if (verify.error) throw verify.error;
          const {
            data: { session },
          } = await supabase.auth.getSession();
          const aal = (session as unknown as { aal?: string } | null)?.aal ?? null;
          return { data: { aal } };
        } catch (err) {
          const e = err as { message?: string };
          return {
            error: {
              message: e?.message ?? "MFA verification failed",
              details: "",
              hint: "",
              code: "auth_mfa_error",
            },
          };
        }
      },
      // Narrow invalidation: verify only changes the session AAL and the
      // factor verification status. The profile endpoint re-reads the
      // session AAL internally, so subsequent profile reads will see aal2
      // without needing an invalidation-driven refetch.
      invalidatesTags: [{ type: "Auth", id: "mfa-factors" }],
    }),

    /**
     * Issues an MFA challenge for a factor. For SMS factors this is what
     * actually sends the Twilio message — there's no separate "send SMS"
     * endpoint in GoTrue. The caller stores the returned `challengeId`
     * and feeds it to `verifyMfaWithChallenge` when the user types the
     * code that arrives on their phone.
     *
     * Not invalidating mfa-factors here: a challenge doesn't change
     * factor state, only the pending challenge record. Avoids a
     * refetch storm if the user clicks "Resend SMS" multiple times.
     */
    issueMfaChallenge: builder.mutation<{ challengeId: string }, { factorId: string }>({
      queryFn: async ({ factorId }) => {
        try {
          const { data, error } = await supabase.auth.mfa.challenge({ factorId });
          if (error) throw error;
          if (!data?.id) throw new Error("MFA challenge returned no id");
          return { data: { challengeId: data.id } };
        } catch (err) {
          const e = err as { message?: string };
          return {
            error: {
              message: e?.message ?? "MFA challenge failed",
              details: "",
              hint: "",
              code: "auth_mfa_error",
            },
          };
        }
      },
    }),

    /**
     * Verifies a code against a previously-issued challenge. Use this for
     * SMS where the challenge was issued upfront (when the user picked
     * the factor) and the SMS has already been sent — verifying with the
     * stored `challengeId` instead of calling `challengeAndVerify`
     * avoids a second SMS and the race where the user's typed code is
     * checked against a fresh challenge they don't have a code for yet.
     */
    verifyMfaWithChallenge: builder.mutation<
      { aal: string | null },
      VerifyMfaArgs & { challengeId: string }
    >({
      queryFn: async ({ factorId, challengeId, code }) => {
        try {
          const { error } = await supabase.auth.mfa.verify({
            factorId,
            challengeId,
            code,
          });
          if (error) throw error;
          const {
            data: { session },
          } = await supabase.auth.getSession();
          const aal = (session as unknown as { aal?: string } | null)?.aal ?? null;
          return { data: { aal } };
        } catch (err) {
          const e = err as { message?: string };
          return {
            error: {
              message: e?.message ?? "MFA verification failed",
              details: "",
              hint: "",
              code: "auth_mfa_error",
            },
          };
        }
      },
      invalidatesTags: [{ type: "Auth", id: "mfa-factors" }],
    }),

    /** Unenrolls a specific factor. */
    unenrollMfa: builder.mutation<{ id: string }, { factorId: string }>({
      queryFn: async ({ factorId }) => {
        try {
          const { error } = await supabase.auth.mfa.unenroll({ factorId });
          if (error) throw error;
          return { data: { id: factorId } };
        } catch (err) {
          const e = err as { message?: string };
          return {
            error: {
              message: e?.message ?? "MFA unenroll failed",
              details: "",
              hint: "",
              code: "auth_mfa_error",
            },
          };
        }
      },
      invalidatesTags: [{ type: "Auth", id: "mfa-factors" }],
    }),
  }),
});

export const {
  useGetMyProfileAndRolesQuery,
  useListMfaFactorsQuery,
  useEnrollTotpMutation,
  useVerifyTotpMutation,
  useIssueMfaChallengeMutation,
  useVerifyMfaWithChallengeMutation,
  useUnenrollMfaMutation,
} = authApi;
